// /src/SecurityContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useAuth } from "./authContext";
import { securitySocket } from "../utils/Socket";
import { startHeartbeat } from "./wsRecovery/heartbeat";
import {
  resumeAll,
  clearResumeRegistry,
  registerSocket,
} from "./wsRecovery/wsResume";
import {
  setResumePointer,
  updateResumeSeq,
  clearResumeStore,
} from "./wsRecovery/wsResumeStore";

const SecurityContext = createContext();
export const useSecurity = () => useContext(SecurityContext);

const CACHE_KEY = "security.permissions.cache.v3";
const VIEW_AS_KEY = "security.viewAs.userId.v1";
export const VIEW_AS_HEADER = "X-View-As-UserId";

function readPersistedViewAsUserId() {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function ts() {
  return new Date().toISOString();
}

export const SecurityProvider = ({ children, preloadedPermissions }) => {
  const { isLoggedIn, userId } = useAuth();
  const socketRef = useRef(null);
  const initialViewAsUserId = readPersistedViewAsUserId();
  const viewAsUserIdRef = useRef(initialViewAsUserId);

  // restore permissions from cache if available
  const [permissions, setPermissions] = useState(
    preloadedPermissions?.permissions || []
  );

  const [isSuperAdmin, setIsSuperAdmin] = useState(
    preloadedPermissions?.isSuperAdmin || false
  );
  const [isGuest, setIsGuest] = useState(
    preloadedPermissions?.isGuest || false
  );
  const [loading, setLoading] = useState(!preloadedPermissions);
  const [permissionsVersion, setPermissionsVersion] = useState(0);

  const [viewAsUserId, setViewAsUserId] = useState(initialViewAsUserId);
  const [viewAsPermissions, setViewAsPermissions] = useState([]);
  const [viewAsIsSuperAdmin, setViewAsIsSuperAdmin] = useState(false);
  const [viewAsIsGuest, setViewAsIsGuest] = useState(false);
  const [viewAsLoading, setViewAsLoading] = useState(false);
  const [viewAsLoadedForUserId, setViewAsLoadedForUserId] = useState(null);

  useEffect(() => {
    viewAsUserIdRef.current = viewAsUserId;
  }, [viewAsUserId]);

  // Register before children's useEffect so the first view-as snapshot already
  // carries X-View-As-UserId (useEffect would race and return the admin identity).
  useLayoutEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      if (config?.skipViewAs === true) {
        if (config.headers?.[VIEW_AS_HEADER]) {
          delete config.headers[VIEW_AS_HEADER];
        }
        return config;
      }
      const v = viewAsUserIdRef.current;
      if (v) {
        config.headers = config.headers || {};
        config.headers[VIEW_AS_HEADER] = String(v);
      } else if (config?.headers?.[VIEW_AS_HEADER]) {
        delete config.headers[VIEW_AS_HEADER];
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // English comment: track last WS sequence for resume
  const lastSeqRef = useRef(0);

  // -------------------------------------------------------------------
  // Reconnect WS on auth refresh
  // -------------------------------------------------------------------
  useEffect(() => {
    const handler = () => {
      const socket = socketRef.current;
      if (!socket) return;
      if (!isLoggedIn) return;

      console.log(
        `[${ts()}][SecurityContext][WS] Auth refreshed → reconnect security WS`
      );

      try {
        socket.connect();
        startHeartbeat(socket);
      } catch (err) {}
    };

    window.addEventListener("auth-ws-refreshed", handler);
    return () => window.removeEventListener("auth-ws-refreshed", handler);
  }, [isLoggedIn]);

  // -------------------------------------------------------------------
  // Cache helper
  // -------------------------------------------------------------------
  const cachePermissions = useCallback((perms) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(perms || []));
    } catch {}
  }, []);

  // -------------------------------------------------------------------
  // Reset state when user logs out
  // -------------------------------------------------------------------
  const resetState = useCallback(() => {
    console.warn(`[${ts()}][SecurityContext] Reset state`);
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(VIEW_AS_KEY);

    viewAsUserIdRef.current = null;

    setPermissions([]);
    setIsSuperAdmin(false);
    setIsGuest(false);
    setPermissionsVersion(0);
    setLoading(false);
    lastSeqRef.current = 0;

    setViewAsUserId(null);
    setViewAsPermissions([]);
    setViewAsIsSuperAdmin(false);
    setViewAsIsGuest(false);
    setViewAsLoading(false);
    setViewAsLoadedForUserId(null);

    // English comment: clear WS-resume registry for security pointers
    clearResumeRegistry();
    clearResumeStore();

    if (socketRef.current) {
      try {
        console.warn(
          `[${ts()}][SecurityContext][WS] Forced disconnect()`
        );
        socketRef.current.disconnect();
      } catch {}
      socketRef.current = null;
    }
  }, []);

  // -------------------------------------------------------------------
  // Initial permissions fetch via REST
  // -------------------------------------------------------------------
  const fetchPermissions = useCallback(async () => {
    try {
      const res = await axios.post(
        "/api/security/user-permissions",
        {},
        { withCredentials: true }
      );

      const raw = res.data?.permissions || res.data || [];

      const cleaned = raw
        .map((p) => ({
          resourceKey: p?.resourceKey ?? p?.resource ?? null,
          action: p?.action ?? p?.permission ?? null,
        }))
        .filter((p) => p.resourceKey && p.action);

      const nextSuperAdmin = Boolean(res.data?.isSuperAdmin);
      const nextGuest = Boolean(res.data?.isGuest);
      setPermissions(cleaned);
      setIsSuperAdmin(nextSuperAdmin);
      setIsGuest(nextGuest);
      cachePermissions(cleaned);
      setPermissionsVersion((v) => v + 1);
      setLoading(false);
      return {
        permissions: cleaned,
        isSuperAdmin: nextSuperAdmin,
        isGuest: nextGuest,
      };
    } catch (err) {
      console.error(
        `[${ts()}][SecurityContext] fetchPermissions error`,
        err
      );
      setLoading(false);
      return null;
    }
  }, [cachePermissions]);

  const stopViewAs = useCallback(() => {
    try {
      sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {}
    // Interceptor reads this ref synchronously — must clear before setState or
    // in-flight / immediate requests still send X-View-As-UserId until the next paint.
    viewAsUserIdRef.current = null;
    setViewAsUserId(null);
    setViewAsPermissions([]);
    setViewAsIsSuperAdmin(false);
    setViewAsIsGuest(false);
    setViewAsLoading(false);
    setViewAsLoadedForUserId(null);
    setPermissionsVersion((v) => v + 1);
    void fetchPermissions();
  }, [fetchPermissions]);

  const startViewAs = useCallback(
    async (targetUserId) => {
      const nextId = Number(targetUserId);
      if (!Number.isFinite(nextId) || nextId <= 0) return;

      setViewAsLoadedForUserId(null);
      setViewAsLoading(true);
      try {
        const res = await axios.get("/api/admin/get-user-permissions", {
          withCredentials: true,
          params: { userId: nextId },
        });

        const raw = Array.isArray(res.data?.permissions)
          ? res.data.permissions
          : Array.isArray(res.data)
          ? res.data
          : [];

        const cleaned = raw
          .map((p) => ({
            resourceKey: p?.resourceKey ?? p?.resource ?? null,
            action: p?.action ?? p?.permission ?? null,
          }))
          .filter((p) => p.resourceKey && p.action);

        viewAsUserIdRef.current = nextId;
        setViewAsUserId(nextId);
        setViewAsPermissions(cleaned);
        setViewAsIsSuperAdmin(Boolean(res.data?.isSuperAdmin));
        setViewAsIsGuest(Boolean(res.data?.isGuest));
        setViewAsLoadedForUserId(nextId);
        try {
          sessionStorage.setItem(VIEW_AS_KEY, String(nextId));
        } catch {}
        setPermissionsVersion((v) => v + 1);
      } catch (err) {
        console.error(`[${ts()}][SecurityContext] startViewAs error`, err);
        stopViewAs();
      } finally {
        setViewAsLoading(false);
      }
    },
    [stopViewAs]
  );

  // -------------------------------------------------------------------
  // React to login/logout state
  // -------------------------------------------------------------------
  useEffect(() => {
    if (isLoggedIn) {
      fetchPermissions();
    } else {
      resetState();
    }
  }, [isLoggedIn, fetchPermissions, resetState]);

  // If a view-as userId was persisted, rehydrate its permissions after login.
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!viewAsUserId) return;
    if (viewAsLoading) return;
    if (viewAsLoadedForUserId === viewAsUserId) return;
    startViewAs(viewAsUserId);
  }, [isLoggedIn, startViewAs, viewAsLoadedForUserId, viewAsLoading, viewAsUserId]);

  // -------------------------------------------------------------------
  // WS connection + WS resume
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isLoggedIn) return;

    const socket = securitySocket;
    socketRef.current = socket;

    if (!socket.connected) {
      socket.connect();
      startHeartbeat(socket);
    }

    // English comment: register resume pointer for security permissions
    setResumePointer("security-permissions", {});

    // English comment: register WS-resume callback (SQL journal)
    registerSocket("security-permissions", async () => {
      try {
        const { data } = await axios.post(
          "/api/ws-resume/security-permissions",
          { sinceSeq: lastSeqRef.current },
          { withCredentials: true }
        );

        if (data?.ok) {
          if (typeof data.maxSeq === "number") {
            lastSeqRef.current = Math.max(
              lastSeqRef.current,
              data.maxSeq
            );
            updateResumeSeq("security-permissions", lastSeqRef.current);
          }

          if (data.hasChanges) {
            await fetchPermissions();
          }
        }
      } catch (err) {
        console.error(
          `[${ts()}][SecurityContext][WS] resume failed`,
          err
        );
      }
    });

    // -----------------------------------------------------------------
    // On connect → trigger resumeAll()
    // -----------------------------------------------------------------
    const onConnect = () => {
      // English comment: trigger global WS-resume for all registered channels
      resumeAll();
    };

    const onConnectError = (err) => {
      console.error(
        `[${ts()}][SecurityContext][WS] connect_error:`,
        err?.message
      );
    };

    const onLogout = () => {
      resetState();
      window.dispatchEvent(new CustomEvent("logout-401"));
    };

    const onUpdate = (payload) => {
      if (payload?.wsSeq && payload.wsSeq > lastSeqRef.current) {
        lastSeqRef.current = payload.wsSeq;
        updateResumeSeq("security-permissions", payload.wsSeq);
      }

      try {
        const raw =
          payload?.permissions?.permissions ||
          payload?.permissions ||
          [];

        const cleaned = raw
          .map((p) => ({
            resourceKey: p?.resourceKey ?? p?.resource ?? null,
            action: p?.action ?? p?.permission ?? null,
          }))
          .filter((p) => p.resourceKey && p.action);

        const nextSuperAdmin = Boolean(payload?.permissions?.isSuperAdmin);
        const nextGuest = Boolean(payload?.permissions?.isGuest);

        // English comment: after tab sleep/wake, a stale empty WS push must not wipe
        // permissions that were loaded from REST moments earlier.
        if (!nextSuperAdmin && cleaned.length === 0) {
          let hadCachedPerms = false;
          try {
            const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "[]");
            hadCachedPerms = Array.isArray(cached) && cached.length > 0;
          } catch {}

          if (hadCachedPerms) {
            console.warn(
              `[${ts()}][SecurityContext] Ignoring empty security:update — refetching from REST`
            );
            void fetchPermissions();
            return;
          }
        }

        setPermissions(cleaned);
        setIsSuperAdmin(nextSuperAdmin);
        setIsGuest(nextGuest);
        cachePermissions(cleaned);
        setPermissionsVersion((v) => v + 1);
      } catch (err) {
        console.error(
          `[${ts()}][SecurityContext] Error parsing WS security:update:`,
          err
        );
      }
    };

    const onDisconnect = (reason) => {
      // English comment: simple debug log for WS disconnect
      console.warn(
        `[${ts()}][SecurityContext][WS] Disconnected (${reason}) socket=${socket.id}`
      );
    };

    socket.on("connect", onConnect);
    socket.on("connect_error", onConnectError);
    socket.on("security:logout", onLogout);
    socket.on("security:update", onUpdate);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      socket.off("security:logout", onLogout);
      socket.off("security:update", onUpdate);
      socket.off("disconnect", onDisconnect);
    };
  }, [isLoggedIn, fetchPermissions, cachePermissions, resetState, userId]);

  // -------------------------------------------------------------------
  // Permission checker
  // -------------------------------------------------------------------
  const realUserHasPermission = useCallback(
    (resourceKey, action) => {
      if (isSuperAdmin) return true;
      if (!Array.isArray(permissions) || permissions.length === 0)
        return false;

      return permissions.some(
        (p) => p.resourceKey === resourceKey && p.action === action
      );
    },
    [permissions, isSuperAdmin]
  );

  const isViewAsActive = Boolean(viewAsUserId);

  /**
   * Permissions of the effective subject: logged-in user, or the "view as" user
   * (includes write/execute/create). Use for UI that must match what that user sees.
   * Production write safety stays in feature code via `allowsViewAsMutations()`.
   */
  const subjectHasPermission = useCallback(
    (resourceKey, action) => {
      if (!isViewAsActive) return realUserHasPermission(resourceKey, action);
      if (viewAsIsSuperAdmin) return true;
      if (!Array.isArray(viewAsPermissions) || viewAsPermissions.length === 0)
        return false;
      return viewAsPermissions.some(
        (p) => p.resourceKey === resourceKey && p.action === action
      );
    },
    [isViewAsActive, realUserHasPermission, viewAsIsSuperAdmin, viewAsPermissions]
  );

  /**
   * UI visibility follows the subject (including write/execute/create in view-as),
   * so admins can preview permission-gated chrome exactly as the member would see it.
   * Do not rely on this alone to prevent API writes on production — gate mutations
   * with `allowsViewAsMutations()` / feature-level locks (see GamerIdentityCheck).
   */
  const uiHasPermission = useCallback(
    (resourceKey, action) => subjectHasPermission(resourceKey, action),
    [subjectHasPermission]
  );

  // -------------------------------------------------------------------
  // final exposed API
  // -------------------------------------------------------------------
  return (
    <SecurityContext.Provider
      value={{
        permissions,
        permissionsVersion,
        uiHasPermission,
        userHasPermission: uiHasPermission,
        subjectHasPermission,
        realUserHasPermission,
        isSuperAdmin,
        isGuest,
        isViewAsActive,
        viewAsUserId,
        viewAsIsSuperAdmin,
        viewAsIsGuest,
        viewAsLoading,
        startViewAs,
        stopViewAs,
        refreshPermissions: fetchPermissions,
        loading,
      }}
    >
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityProvider;
